/**
 * stereographicPipeline — stereographic projection of a sphere.
 *
 * Stereographic projection maps points on a sphere to a plane by
 * projecting through the sphere's south pole. Lines on the sphere
 * that don't pass through the pole become circles on the plane.
 *
 * We sample a sphere with a noise pattern (fbm of latitude/longitude)
 * and project it to the plane. The result is a beautiful warped
 * pattern with circular isoclines.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface StereographicPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

// Project from plane (x, y) to sphere. The inverse projection.
// We take a point (x, y) on the plane and find a point on the unit sphere.
// Stereographic projection formula:
//   x' = 2x / (x^2 + y^2 + 1)
//   y' = 2y / (x^2 + y^2 + 1)
//   z' = (x^2 + y^2 - 1) / (x^2 + y^2 + 1)
// The inverse:
//   x = x' / (1 - z')
//   y = y' / (1 - z')
// Here we go the other way: take (x, y) on the plane, find a (x', y', z') on
// the sphere, then sample a noise function on the sphere (using
// spherical coords).
void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Scale to fit the projection (radius ~1.5 fits the screen).
  p *= 1.5;
  // Apply a slow rotation (orbiting the sphere).
  float rot = uTime * 0.15;
  float ca = cos(rot), sa = sin(rot);
  p = mat2(ca, -sa, sa, ca) * p;

  // Forward stereographic: (x, y) on plane → (x', y', z') on sphere.
  float r2 = dot(p, p);
  float denom = 1.0 + r2;
  vec3 sphere = vec3(
    2.0 * p.x / denom,
    2.0 * p.y / denom,
    (r2 - 1.0) / denom
  );
  // Convert sphere position to spherical coords.
  // Latitude: asin(y), longitude: atan(z, x).
  float lat = asin(clamp(sphere.y, -1.0, 1.0));
  float lon = atan(sphere.z, sphere.x);
  // Sample FBM in spherical coords (with time animation).
  float n = fbm(vec2(lon * 1.5, lat * 1.5) + vec2(uTime * 0.2, uTime * 0.1));

  // Color.
  vec3 col1 = vec3(0.05, 0.15, 0.30);  // deep blue
  vec3 col2 = vec3(0.40, 0.80, 0.95);  // cyan
  vec3 col3 = vec3(0.95, 0.55, 0.85);  // pink
  vec3 col = mix(col1, col2, smoothstep(0.2, 0.6, n));
  col = mix(col, col3, smoothstep(0.6, 0.9, n));
  // Audio: brighten on high.
  col *= 1.0 + high * 0.4;
  // Audio: shift on bass.
  col = mix(col, col.bgr, bass * 0.2);

  // Concentric circles at fixed radii (isoclines of projection).
  float isoDist = abs(length(p) - 0.5);
  float iso = exp(-isoDist * isoDist * 5000.0);
  col += vec3(1.0) * iso * 0.15;

  // Vignette.
  vec2 vp = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float vig = 1.0 - dot(vp, vp) * 0.2;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeStereographicBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeStereographicRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createStereographicPipeline(gl: WebGL2RenderingContext): StereographicPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawStereographic(
  gl: WebGL2RenderingContext,
  pipe: StereographicPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
